/**
 * hooks/useResizeHandles.ts — wires the three pane-divider handles in
 * Terminal.tsx to drag-resize the surrounding panes.
 *
 * Handles:
 *   handle-lc  (horizontal) :: drag right to widen left-pane    [updates --left-w in px]
 *   handle-cr  (horizontal) :: drag left  to widen right-pane   [updates --right-w in px]
 *   handle-cv  (vertical)   :: drag down  to grow chart-pane    [updates --chart-h in %]
 *
 * Each handle attaches a mousedown listener that:
 *   1. Captures the starting cursor position and the current pane size
 *   2. Adds document-level mousemove/mouseup so the drag survives
 *      moving off the 5px handle target
 *   3. On move, applies the new size via document.documentElement.style
 *      (CSS variables) which the .left-pane / .right-pane / .chart-pane
 *      rules in theme.css read
 *   4. On release, persists the layout to localStorage so it survives
 *      reload, and removes the listeners
 *
 * Bounds are enforced by the min-width / max-width / min-height CSS
 * rules on each pane — the drag math doesn't need to know them.
 */
import { useEffect } from 'react';

const STORAGE_KEY = 'omnistrat-layout';

interface Layout {
  leftW?:  number;  // px
  rightW?: number;  // px
  chartH?: number;  // percent 0..1
}

function loadLayout(): Layout {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function saveLayout(l: Layout) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(l)); } catch { /* quota */ }
}

function applyLayout(l: Layout) {
  const r = document.documentElement;
  if (typeof l.leftW  === 'number') r.style.setProperty('--left-w',  l.leftW  + 'px');
  if (typeof l.rightW === 'number') r.style.setProperty('--right-w', l.rightW + 'px');
  if (typeof l.chartH === 'number') r.style.setProperty('--chart-h', (l.chartH * 100) + '%');
}

export function useResizeHandles() {
  useEffect(() => {
    // Restore persisted layout on first mount.
    const saved = loadLayout();
    applyLayout(saved);
    const live: Layout = { ...saved };

    // Helper to grab the current rendered width/height of a selector
    // — used to seed the drag math from the actual pane size.
    const getRect = (sel: string) => {
      const el = document.querySelector(sel);
      return el ? el.getBoundingClientRect() : null;
    };

    type Axis = 'x' | 'y';
    interface HandleSpec {
      id:    string;
      axis:  Axis;
      apply: (deltaPx: number, startSize: number, containerSize: number) => void;
      getStartSize: () => number;
      getContainerSize: () => number;
    }

    const specs: HandleSpec[] = [
      // Left-pane: drag handle right -> wider left, drag left -> narrower.
      {
        id:   'handle-lc',
        axis: 'x',
        getStartSize:     () => getRect('.left-pane')?.width  ?? 190,
        getContainerSize: () => getRect('.main-grid')?.width  ?? 1,
        apply: (d, start) => {
          const next = Math.round(start + d);
          live.leftW = next;
          document.documentElement.style.setProperty('--left-w', next + 'px');
        },
      },
      // Right-pane: drag handle left -> wider right (inverse of cursor).
      {
        id:   'handle-cr',
        axis: 'x',
        getStartSize:     () => getRect('.right-pane')?.width ?? 290,
        getContainerSize: () => getRect('.main-grid')?.width  ?? 1,
        apply: (d, start) => {
          const next = Math.round(start - d);   // inverse
          live.rightW = next;
          document.documentElement.style.setProperty('--right-w', next + 'px');
        },
      },
      // Chart-pane: drag handle down -> taller chart (smaller bottom).
      // Stored as 0..1 fraction of the center-pane height for resilience
      // against viewport resize. Ceiling raised to 0.96 so the user can
      // push the chart-pane to nearly full vertical when they want to
      // focus on the candles + indicators (bottom-tabs collapses to a
      // strip just tall enough for the tab labels).
      {
        id:   'handle-cv',
        axis: 'y',
        getStartSize:     () => getRect('.chart-pane')?.height ?? 0,
        getContainerSize: () => getRect('.center-pane')?.height ?? 1,
        apply: (d, start, container) => {
          const newPx  = Math.round(start + d);
          const newPct = Math.max(0.20, Math.min(0.96, newPx / container));
          live.chartH = newPct;
          document.documentElement.style.setProperty('--chart-h', (newPct * 100) + '%');
        },
      },
    ];

    // Per-handle cleanup; collected and run on unmount.
    const detachers: Array<() => void> = [];

    for (const spec of specs) {
      const el = document.getElementById(spec.id);
      if (!el) continue;

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        const startX  = e.clientX;
        const startY  = e.clientY;
        const startSz = spec.getStartSize();
        const contSz  = spec.getContainerSize();
        el.classList.add('dragging');
        document.body.classList.add(spec.axis === 'x' ? 'resizing' : 'resizing-v');

        const onMove = (mv: MouseEvent) => {
          const d = spec.axis === 'x' ? (mv.clientX - startX) : (mv.clientY - startY);
          spec.apply(d, startSz, contSz);
        };
        const onUp = () => {
          el.classList.remove('dragging');
          document.body.classList.remove('resizing');
          document.body.classList.remove('resizing-v');
          document.removeEventListener('mousemove', onMove);
          document.removeEventListener('mouseup', onUp);
          saveLayout(live);
        };

        document.addEventListener('mousemove', onMove);
        document.addEventListener('mouseup', onUp);
      };

      el.addEventListener('mousedown', onMouseDown);
      detachers.push(() => el.removeEventListener('mousedown', onMouseDown));
    }

    return () => { for (const d of detachers) d(); };
  }, []);
}
